import { isEqual } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { getSubCounterpartyScopeFilters, setScopeFilters, SubCounterpartyType } from '../../../admin/sub-counterparty-types/store';
import { FeaturePermission } from '../../../admin/users/store';
import { getUserHasFeaturePermission, getUserHasFeaturePermissionNoAdmin } from '../../../auth/login/store';
import { Toggle } from '../../../shared/toggle';
import { OverflowTooltip } from '../../../shared/tooltip';
import { getOpinion } from '../../my-opinions/store';
import { CounterpartyCoverage, updateIncludedInstanceSubCounterpartyTypes } from '../store';
import styles from './OpinionSection.module.scss';
import { SubCounterpartyConfigColumns } from './SubCounterpartyColumns';
import { OpinionScope } from '../../../admin/opinions/store';

interface SubCounterpartConfigBodyProps {
    counterpartyCoverage: CounterpartyCoverage;
    readOnly: boolean;
    availableSubCounterpartyTypes: SubCounterpartyType[];
    selectedParentCounterpartyType: string;
    selectedSubCounterparty: SubCounterpartyType | null;
}

export const SubCounterpartConfigBody: React.FC<SubCounterpartConfigBodyProps> = ({ readOnly, counterpartyCoverage, availableSubCounterpartyTypes, selectedParentCounterpartyType }) => {
    const dispatch = useAppDispatch();

    const userHasIndustryStandardPermissions = useAppSelector(getUserHasFeaturePermissionNoAdmin([FeaturePermission.MANAGE_INDUSTRY_STANDARD_OPINIONS]));
    const userHasEditSubCounterpartyPermission = useAppSelector(getUserHasFeaturePermission([FeaturePermission.SUB_COUNTERPARTY_TYPE]));
    const opinion = useAppSelector(getOpinion);
    const scopeFilters = useAppSelector(getSubCounterpartyScopeFilters);

    const isIndustryStandardOpinion = useMemo(() => !opinion?.bespoke || false, [opinion]);
    const scope = useMemo(() => opinion?.scope || null, [opinion]);

    const showToggles = useMemo(() => !readOnly && ((userHasEditSubCounterpartyPermission && !isIndustryStandardOpinion) || (isIndustryStandardOpinion && userHasIndustryStandardPermissions)), [userHasEditSubCounterpartyPermission, userHasIndustryStandardPermissions, isIndustryStandardOpinion, readOnly]);

    const configHeight = useMemo(() => {
        if (showToggles) {
            return 'calc(100% - 40px)';
        }
        return '100%';
    }, [showToggles]);

    const subCounterpartyTypes = useMemo(() => {
        if (isIndustryStandardOpinion) {
            return availableSubCounterpartyTypes.filter(({ isSystem, parentCounterpartyType, scopes }) => isSystem && parentCounterpartyType === selectedParentCounterpartyType && (isNull(scope) || scopes.includes(scope)));
        }
        return availableSubCounterpartyTypes.filter(({ parentCounterpartyType, scopes }) => parentCounterpartyType === selectedParentCounterpartyType && (isNull(scope) || scopes.includes(scope)));
    }, [availableSubCounterpartyTypes, isIndustryStandardOpinion, selectedParentCounterpartyType, scope]);

    const outOfScopeSubCounterpartyTypes = useMemo(() => {
        if (isIndustryStandardOpinion) {
            return availableSubCounterpartyTypes.filter(({ isSystem, parentCounterpartyType, scopes }) => isSystem && parentCounterpartyType === selectedParentCounterpartyType && (!isNull(scope) && !scopes.includes(scope)));
        }
        return availableSubCounterpartyTypes.filter(({ parentCounterpartyType, scopes }) => parentCounterpartyType === selectedParentCounterpartyType && (!isNull(scope) && !scopes.includes(scope)));
    }, [availableSubCounterpartyTypes, isIndustryStandardOpinion, selectedParentCounterpartyType, scope]);

    const includedIds = useMemo(() => counterpartyCoverage.standardOptions.find(({ label }) => label === selectedParentCounterpartyType)?.includedSubCounterpartyTypeIds || [], [counterpartyCoverage, selectedParentCounterpartyType]);
    const allSystemIds = useMemo(() => subCounterpartyTypes.map(({ subCounterpartyTypeId }) => subCounterpartyTypeId!), [subCounterpartyTypes]);

    const includeAllSubCounterpartyTypes = useCallback((checked: boolean) => dispatch(updateIncludedInstanceSubCounterpartyTypes(selectedParentCounterpartyType, checked ? allSystemIds : [])), [dispatch, allSystemIds, selectedParentCounterpartyType]);

    const allScopesIncluded = useMemo(() => isEqual(scopeFilters, Object.values(OpinionScope)), [scopeFilters]);

    const allCounterpartyTypesAreIncluded = useMemo(() => isEqual(includedIds, allSystemIds), [includedIds, allSystemIds]);
    const toggleIncludeLabel = useMemo(() => `${allCounterpartyTypesAreIncluded ? 'Exclude' : 'Include'} All`, [allCounterpartyTypesAreIncluded]);
    const toggleScopeLabel = useMemo(() => `${allScopesIncluded ? 'Exclude' : 'Include'} Out of Scope`, [allScopesIncluded]);

    const toggleScopeFilters = useCallback(() => {
        let updatedScopeFilters = Object.values(OpinionScope);
        if (allScopesIncluded && !isNull(opinion) && !isNull(opinion.scope)) {
            updatedScopeFilters = [opinion.scope];
        }
        dispatch(setScopeFilters(updatedScopeFilters));
    }, [dispatch, allScopesIncluded, opinion]);

    return (
        <div className={styles.subCounterpartyTypesIncludedItemsSectionWrapper}>
            {showToggles &&
                <div className={styles.togglesWrapper}>
                    <div className={styles.toggleOutOfScopeWrapper}>
                        <OverflowTooltip className={styles.toggleOutOfScopeLabel} overlayText={toggleScopeLabel} />
                        <Toggle checked={allScopesIncluded} onChange={toggleScopeFilters} />
                    </div>
                    <div className={styles.selectAllWrapper}>
                        <OverflowTooltip className={styles.selectAllLabel} overlayText={toggleIncludeLabel} />
                        <Toggle checked={allCounterpartyTypesAreIncluded || false} onChange={includeAllSubCounterpartyTypes} />
                    </div>
                </div>
            }
            <SubCounterpartyConfigColumns
                counterpartyCoverage={counterpartyCoverage}
                readOnly={readOnly}
                subCounterpartyTypes={subCounterpartyTypes}
                outOfScopeSubCounterpartyTypes={outOfScopeSubCounterpartyTypes}
                height={configHeight}
                selectedParentCounterpartyType={selectedParentCounterpartyType}
                userHasIndustryStandardPermissions={userHasIndustryStandardPermissions}
                scope={scope}
            />
        </div>
    );
};
